import { useState } from 'react'

export interface PartyValues {
  name: string
  phone: string
  address: string
  location: string
  gstin: string
  email: string
  route?: string
  creditDays?: string
}

interface Props {
  kind: 'supplier' | 'customer'
  initial: PartyValues
  /** Resolves when saved; throws (or rejects) with an Error whose message is shown in the dialog. */
  onSave: (values: PartyValues) => Promise<void>
  onClose: () => void
}

/** Edit dialog for a supplier (Purchase Entry) or a customer / shop (Billing). */
export function PartyEditModal({ kind, initial, onSave, onClose }: Props) {
  const [v, setV] = useState<PartyValues>(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const isCustomer = kind === 'customer'
  const set = (patch: Partial<PartyValues>) => setV((cur) => ({ ...cur, ...patch }))

  const save = async () => {
    const name = v.name.replace(/\s+/g, ' ').trim()
    if (!name) return setError(isCustomer ? 'Shop / customer name is required' : 'Supplier name is required')
    const phone = v.phone.replace(/\s+/g, '')
    if (phone && phone !== initial.phone.replace(/\s+/g, '') && !/^\d{10}$/.test(phone)) return setError('Phone must be a 10-digit mobile number')
    const gstin = v.gstin.replace(/\s+/g, '').toUpperCase()
    if (gstin && gstin !== initial.gstin.toUpperCase() && !/^[0-9]{2}[A-Z0-9]{13}$/.test(gstin)) return setError('GSTIN should be 15 characters (e.g. 33ABCDE1234F1Z5)')
    if (isCustomer && v.creditDays && v.creditDays.trim() !== '' && !/^\d{1,3}$/.test(v.creditDays.trim())) return setError('Credit days must be a whole number from 0 to 365')
    setSaving(true); setError('')
    try {
      await onSave({ ...v, name, phone, gstin })
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Could not save the details')
      setSaving(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => !saving && onClose()}>
      <div className="card" style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflow: 'auto', margin: 0 }} onClick={(e) => e.stopPropagation()} data-testid="party-edit-modal">
        <div className="field-label-row">
          <h2 style={{ marginBottom: 0 }}>{isCustomer ? 'Edit customer details' : 'Edit supplier details'}</h2>
          <button type="button" className="btn-ghost" style={{ padding: '2px 9px' }} onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>
        {error && <div className="alert alert-error" style={{ marginTop: 8 }}>{error}</div>}
        <label style={{ marginTop: 8 }}>{isCustomer ? 'Shop / customer name' : 'Supplier name'}</label>
        <input type="text" value={v.name} onChange={(e) => set({ name: e.target.value })} data-testid="party-name" />
        {v.name.replace(/\s+/g, ' ').trim() !== initial.name && <div className="hint" style={{ marginTop: 0 }}>Renaming also changes the name on {isCustomer ? 'all of this customer’s invoices' : 'all of this supplier’s bills'}.</div>}
        <div className="grid grid-cols-2 gap-3" style={{ marginTop: 10 }}>
          <div>
            <label>Phone</label>
            <input type="text" inputMode="numeric" placeholder="10-digit mobile" value={v.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="party-phone" />
          </div>
          <div>
            <label>GSTIN <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— optional</small></label>
            <input type="text" maxLength={15} value={v.gstin} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} data-testid="party-gstin" />
          </div>
        </div>
        <label style={{ marginTop: 10 }}>Address <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— door no., street, town, PIN</small></label>
        <textarea rows={2} value={v.address} onChange={(e) => set({ address: e.target.value })} data-testid="party-address" />
        <div className="grid grid-cols-2 gap-3" style={{ marginTop: 10 }}>
          <div>
            <label>Location / area</label>
            <input type="text" value={v.location} onChange={(e) => set({ location: e.target.value })} />
          </div>
          <div>
            <label>Email <small style={{ fontWeight: 400, color: 'var(--ink-soft)' }}>— optional</small></label>
            <input type="text" value={v.email} onChange={(e) => set({ email: e.target.value })} />
          </div>
          {isCustomer && (
            <>
              <div>
                <label>Route</label>
                <input type="text" value={v.route || ''} onChange={(e) => set({ route: e.target.value })} />
              </div>
              <div>
                <label>Credit days</label>
                <input type="text" inputMode="numeric" value={v.creditDays || ''} onChange={(e) => set({ creditDays: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
              </div>
            </>
          )}
        </div>
        <div className="btnrow">
          <button type="button" className="btn-accent" onClick={save} disabled={saving} data-testid="party-save">{saving ? 'Saving…' : 'Save details'}</button>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
        <div className="hint">The change is saved on the {isCustomer ? 'customer' : 'supplier'} record, so it applies everywhere — not just this {isCustomer ? 'sale' : 'bill'}.</div>
      </div>
    </div>
  )
}
